// frontend/src/ChatSettingsModal.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import './ChatSettingsModal.css';
import './FileExplorerTab.css';
import DocumentViewerModal from './DocumentViewerModal';

// --- Icons ---
const FolderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-folder-fill file-icon" viewBox="0 0 16 16">
    <path d="M9.828 3h3.982a2 2 0 0 1 1.992 2.181l-.637 7A2 2 0 0 1 13.174 14H2.826a2 2 0 0 1-1.991-1.819l-.637-7a1.99 1.99 0 0 1 .342-1.31L.5 3a2 2 0 0 1 2-2h3.672a2 2 0 0 1 1.414.586l.828.828A2 2 0 0 0 9.828 3"/>
  </svg>
);
const FileIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-file-earmark-text-fill file-icon" viewBox="0 0 16 16">
    <path d="M9.293 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.707A1 1 0 0 0 13.707 4L10 .293A1 1 0 0 0 9.293 0M9.5 3.5v-2l3 3h-2a1 1 0 0 1-1-1M4.5 9a.5.5 0 0 1 0-1h7a.5.5 0 0 1 0 1zm0 2a.5.5 0 0 1 0-1h7a.5.5 0 0 1 0 1zm0 2a.5.5 0 0 1 0-1h4a.5.5 0 0 1 0 1z"/>
  </svg>
);
const SortAscIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" className="bi bi-caret-up-fill sort-icon" viewBox="0 0 16 16">
        <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z"/>
    </svg>
);
const SortDescIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" className="bi bi-caret-down-fill sort-icon" viewBox="0 0 16 16">
        <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/>
    </svg>
);
// --- End Icons ---

// --- Configuration ---
const BASE_URL = process.env.REACT_APP_SERVER_URL || '';

// --- Modal Styles Definition ---
const customModalStyles = { 
    content: {
        top: '50%', left: '50%', right: 'auto', bottom: 'auto', marginRight: '-50%', transform: 'translate(-50%, -50%)',
        maxWidth: '850px', width: '90%', maxHeight: '85vh', overflow: 'hidden', padding: '0',
        borderRadius: '8px', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)', border: 'none',
        background: '#fff', display: 'flex', flexDirection: 'column',
     },
    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 1000 },
};
// --- End Modal Styles ---

// Default tool structure for initialization
// const DEFAULT_TOOLS_STATE = {
//     relevantText: true, googlesearch: false, queryDatabases: false, exploreKnowledgeGraph: true,
//     searchCollectionKnowledge: true, saveTextToFile: false, listFilesInDirectory: false, readFileContent: false,
//     analyticsEngine: false, Knowledge_query: false, listAllMyFiles: false, image_annotation: false, generate_image: false, createDirectory: true,
// };

const DEFAULT_TOOLS_STATE = {
    relevantText: true, googlesearch: false, queryDatabases: false, exploreKnowledgeGraph: true,
    searchCollectionKnowledge: true, saveTextToFile: false, listFilesInDirectory: false, readFileContent: false,
    analyticsEngine: false, Knowledge_query: false, listAllMyFiles: false, image_annotation: false, generate_image: false, createDirectory: true,
    scrapeWebsite: false,  getStockInformation: false, // ADD THIS
};

const ChatSettingsModal = ({
    isOpen,
    onSave,
    onCancel,
    knowledgeNames = [], 
    initialUseRAG,
    initialSelectedKnowledgeName,
    initialUseText2SQL,
    initialSelectedDbForModal,
    initialUseAgentAPI,
    initialLoopsBeforeHumanInput,
    initialSelectedTools,
    initialSelectedPipeline,
    initialSeeThoughtsAndActions,
    onSelectPrompt = () => {}, 
    initialAttachedFiles,
    authUsername
}) => {
    const [activeTab, setActiveTab] = useState('File Explorer'); 

    // --- Internal State for Modal Edits ---
    const [currentUseRAG, setCurrentUseRAG] = useState(initialUseRAG ?? false);
    const [currentSelectedKnowledgeNameInModal, setCurrentSelectedKnowledgeNameInModal] = useState(initialSelectedKnowledgeName ?? '');
    const [currentUseText2SQL, setCurrentUseText2SQL] = useState(initialUseText2SQL ?? false);
    const [currentSelectedDBInModal, setCurrentSelectedDBInModal] = useState(initialSelectedDbForModal ?? '');
    const [currentUseAgentAPI, setCurrentUseAgentAPI] = useState(initialUseAgentAPI ?? false);
    const [currentLoops, setCurrentLoops] = useState(initialLoopsBeforeHumanInput ?? 1);
    const [currentTools, setCurrentTools] = useState(initialSelectedTools ? { ...DEFAULT_TOOLS_STATE, ...initialSelectedTools } : { ...DEFAULT_TOOLS_STATE });
    const [currentPipeline, setCurrentPipeline] = useState(initialSelectedPipeline ?? '');
    const [currentSeeThoughts, setCurrentSeeThoughts] = useState(initialSeeThoughtsAndActions ?? false);

    // --- File Explorer State ---
    const [currentPath, setCurrentPath] = useState('.');
    const [items, setItems] = useState([]);
    const [selectedFilesForAttachment, setSelectedFilesForAttachment] = useState(initialAttachedFiles || []);
    const [isLoadingFiles, setIsLoadingFiles] = useState(false);
    const [fileError, setFileError] = useState('');
    const [lastClickedFile, setLastClickedFile] = useState(null);
    const fileInputRef = useRef(null);
    const [uploadStatus, setUploadStatus] = useState({ message: '', type: '' });
    const [directorySize, setDirectorySize] = useState({ bytes: 0, human: 'Calculating...' });
    const [isLoadingSize, setIsLoadingSize] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });

    // --- Document Viewer State ---
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [viewingFilePath, setViewingFilePath] = useState(null);

    // --- Data Fetching State ---
    const [uniqueRemoteDbs, setUniqueRemoteDbs] = useState([]);
    const [prompts, setPrompts] = useState([]);
    const [pipelines, setPipelines] = useState([]);
    const [selectedPromptDetails, setSelectedPromptDetails] = useState(null);
    const [showFullPromptText, setShowFullPromptText] = useState(false);
    const [loadingError, setLoadingError] = useState('');

    // --- START: FIX - Re-added missing state definitions ---
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [actionToConfirm, setActionToConfirm] = useState(null);
    // --- END: FIX ---

    const fetchData = useCallback(async (endpoint, setter, name) => {
        if (!isOpen) return; 
        try {
            const response = await axios.get(`${BASE_URL}/api/${endpoint}`, { withCredentials: true });
            setter(response.data || []);
            console.log(`ChatSettingsModal: Fetched ${name}:`, response.data?.length);
        } catch (error) {
            console.error(`ChatSettingsModal: Error fetching ${name}:`, error);
             let errorMsg = `Failed to load ${name}.`;
             if (error.response?.status === 401) errorMsg = `Failed to load ${name}: Unauthorized.`;
             else if (error.response?.status === 404) errorMsg = `Failed to load ${name}: Endpoint not found (/api/${endpoint}).`;
             else if (error.message) errorMsg = `Failed to load ${name}: ${error.message}.`
            setLoadingError(prev => `${prev}\n${errorMsg}`.trim());
        }
    }, [BASE_URL, isOpen]);

    const fetchFiles = useCallback(async (relativePath) => {
        if (!authUsername || !isOpen) return;
        setIsLoadingFiles(true); setFileError(''); setItems([]); setLastClickedFile(null);
        try {
            const encodedPath = encodeURIComponent(relativePath);
            const response = await axios.get(`${BASE_URL}/api/list_user_files?path=${encodedPath}`, { withCredentials: true });
            setItems(response.data || []);
        } catch (error) {
            console.error(`FileExplorer: Error fetching files for path '${relativePath}':`, error);
             let errorMsg = `Failed to load files for '${relativePath}'.`;
             if (error.response?.status === 401) errorMsg += " Unauthorized.";
             else if (error.response?.status === 404) errorMsg = `Directory not found: '${relativePath}'.`;
             else if (error.response?.data?.error) errorMsg = error.response.data.error;
             else if (error.message) errorMsg += ` ${error.message}.`
            setFileError(errorMsg); setItems([]);
        } finally { setIsLoadingFiles(false); }
    }, [BASE_URL, authUsername, isOpen]); 

     const fetchDirectorySize = useCallback(async () => {
        if (!authUsername || !isOpen) { setDirectorySize({ bytes: 0, human: 'N/A' }); return; }
        setIsLoadingSize(true); setDirectorySize(prev => ({ ...prev, human: 'Calculating...' }));
        try {
            const response = await axios.get(`${BASE_URL}/api/user_home_size`, { withCredentials: true });
            if (response.data && typeof response.data.size_bytes === 'number' && response.data.size_human) {
                setDirectorySize({ bytes: response.data.size_bytes, human: response.data.size_human });
            } else { throw new Error("Invalid size data received"); }
        } catch (error) {
            console.error("FileExplorer: Error fetching directory size:", error);
            setDirectorySize({ bytes: 0, human: 'Error' });
            setLoadingError(prev => `${prev}\nFailed to load directory size. ${error.message}`.trim());
        } finally { setIsLoadingSize(false); }
    }, [BASE_URL, authUsername, isOpen]); 

    const handleFileToolsGroupChange = (isChecked) => {
        setCurrentTools(prev => {
            const newTools = { ...prev };
            FILE_TOOLS_GROUP_KEYS.forEach(key => {
                newTools[key] = isChecked;
            });
            return newTools;
        });
    };

    const handleSearchScrapeGroupChange = (isChecked) => {
        setCurrentTools(prev => {
            const newTools = { ...prev };
            SEARCH_SCRAPE_GROUP_KEYS.forEach(key => {
                newTools[key] = isChecked;
            });
            return newTools;
        });
    };

    useEffect(() => {
        if (isOpen) {
            console.log("ChatSettingsModal: Initializing/Re-initializing on open from props.");
            console.log("ChatSettingsModal: Initial Tools Received:", initialSelectedTools);
            console.log("ChatSettingsModal: Initial SeeThoughts Received:", initialSeeThoughtsAndActions);
            
            setCurrentUseRAG(initialUseRAG ?? false);
            setCurrentSelectedKnowledgeNameInModal(initialSelectedKnowledgeName ?? '');
            setCurrentUseText2SQL(initialUseText2SQL ?? false);
            setCurrentSelectedDBInModal(initialSelectedDbForModal ?? '');
            setCurrentUseAgentAPI(initialUseAgentAPI ?? false);
            setCurrentLoops(initialLoopsBeforeHumanInput ?? 1);
            setCurrentTools(initialSelectedTools ? { ...DEFAULT_TOOLS_STATE, ...initialSelectedTools } : { ...DEFAULT_TOOLS_STATE });
            setCurrentPipeline(initialSelectedPipeline ?? '');
            setCurrentSeeThoughts(initialSeeThoughtsAndActions ?? false);
            setSelectedFilesForAttachment(initialAttachedFiles || []);

            setLoadingError('');
            setUniqueRemoteDbs([]); setPrompts([]); setPipelines([]);
            setSelectedPromptDetails(null); setShowFullPromptText(false);
            
            if (activeTab === 'File Explorer' || !activeTab) { 
                setFileError(''); setCurrentPath('.'); setItems([]); setLastClickedFile(null);
                setUploadStatus({ message: '', type: '' });
                setDirectorySize({ bytes: 0, human: 'Calculating...' });
                fetchFiles('.');
                fetchDirectorySize();
            }
            setIsViewerOpen(false); setViewingFilePath(null);

            fetchData('unique_remote_dbs', setUniqueRemoteDbs, 'Databases');
            fetchData('prompts', setPrompts, 'Prompts');
            fetchData('prompt-pipelines', setPipelines, 'Pipelines');
        }
    }, [
        isOpen, activeTab, 
        initialUseRAG, initialSelectedKnowledgeName, initialUseText2SQL, initialSelectedDbForModal,
        initialUseAgentAPI, initialLoopsBeforeHumanInput, initialSelectedTools, initialSelectedPipeline,
        initialSeeThoughtsAndActions, initialAttachedFiles,
        fetchData, fetchFiles, fetchDirectorySize
    ]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const sortedItems = useMemo(() => {
        let sortableItems = [...items];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                if (a.type === 'dir' && b.type !== 'dir') return -1;
                if (a.type !== 'dir' && b.type === 'dir') return 1;
                let valA = a[sortConfig.key];
                let valB = b[sortConfig.key];
                if (sortConfig.key === 'lastModified') {
                    const dateA = (valA && valA !== "N/A") ? new Date(valA.replace(' ', 'T')) : null;
                    const dateB = (valB && valB !== "N/A") ? new Date(valB.replace(' ', 'T')) : null;
                    if (!dateA && !dateB) return 0;
                    if (!dateA) return 1;
                    if (!dateB) return -1;
                    valA = dateA;
                    valB = dateB;
                }
                let comparison = 0;
                if (valA < valB) comparison = -1;
                else if (valA > valB) comparison = 1;
                return sortConfig.direction === 'ascending' ? comparison : comparison * -1;
            });
        }
        return sortableItems;
    }, [items, sortConfig]);

    const buildFullPath = useCallback((basePath, itemName) => {
        if (basePath === '.') return itemName;
        const cleanBase = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
        return `${cleanBase}/${itemName}`;
    }, []);

    const handleFileItemClickInternal = (item) => {
        if (item.type === 'file') {
            setLastClickedFile({ name: item.name, path: buildFullPath(currentPath, item.name), type: 'file' });
        } else {
            setLastClickedFile({ name: item.name, path: buildFullPath(currentPath, item.name), type: 'dir' });
        }
    };

    const handleFileItemDoubleClickInternal = (item) => {
        setUploadStatus({ message: '', type: '' });
        if (item.type === 'dir') {
            const newPath = buildFullPath(currentPath, item.name);
            setCurrentPath(newPath);
            fetchFiles(newPath);
            setSortConfig({ key: 'name', direction: 'ascending' });
        } else {
            const fullPath = buildFullPath(currentPath, item.name);
            setSelectedFilesForAttachment(prev =>
                prev.includes(fullPath) ? prev.filter(p => p !== fullPath) : [...prev, fullPath]
            );
            setLastClickedFile(null);
        }
    };

    const handleNavigateBackInternal = () => {
        if (currentPath === '.') return;
        setUploadStatus({ message: '', type: '' });
        const pathParts = currentPath.split('/').filter(p => p && p !== '.');
        pathParts.pop();
        const newPath = pathParts.length > 0 ? pathParts.join('/') : '.';
        setCurrentPath(newPath);
        fetchFiles(newPath);
        setSortConfig({ key: 'name', direction: 'ascending' });
    };

    const handleOpenFileInternal = () => {
        if (!lastClickedFile || lastClickedFile.type === 'dir' || !authUsername) return;
        setViewingFilePath(lastClickedFile.path);
        setIsViewerOpen(true);
    };

    const handleUploadButtonClickInternal = () => {
        setUploadStatus({ message: '', type: '' });
        fileInputRef.current?.click();
    };
    
    const handleDeleteClickInternal = () => {
        if (!lastClickedFile) return;
        setActionToConfirm({
            type: 'deleteFile',
            item: lastClickedFile,
            message: `Are you sure you want to permanently delete '${lastClickedFile.name}'? This action cannot be undone.`
        });
        setShowConfirmationModal(true);
    };

    const handleFileSelectedForUploadInternal = async (event) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;
        setUploadStatus({ message: `Uploading ${files.length} file(s)...`, type: 'loading' });
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) formData.append('files', files[i]);
        formData.append('relativePath', currentPath);
        try {
            const response = await axios.post(`${BASE_URL}/api/upload_user_files`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true,
            });
            setUploadStatus({ message: response.data.message || 'Upload successful!', type: 'success' });
            fetchFiles(currentPath);
            fetchDirectorySize();
        } catch (error) {
            const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || 'Upload failed.';
            const errorDetails = error.response?.data?.details || error.response?.data?.errors || [];
            const fullError = `${errorMsg}${errorDetails.length > 0 ? ` (${errorDetails.join(', ')})` : ''}`;
            setUploadStatus({ message: fullError, type: 'error' });
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = '';
            setTimeout(() => setUploadStatus({ message: '', type: '' }), 5000);
        }
    };

    const handlePromptSelectChange = (event) => {
        const promptId = event.target.value;
        const selected = prompts.find(p => p.id.toString() === promptId);
        setSelectedPromptDetails(selected || null);
        setShowFullPromptText(false);

        if (selected) {
            if (selected.provider && selected.model) {
                onSelectPrompt({
                    name: selected.prompt_name, text: selected.prompt_text || '',
                    provider: selected.provider, model: selected.model,
                    temperature: selected.temperature, max_tokens: selected.max_tokens,
                    source: 'prompt_specific_llm_settings'
                });
            } else {
                onSelectPrompt({ name: selected.prompt_name, text: selected.prompt_text || '', source: 'prompt_text_only' });
            }
        } else {
            onSelectPrompt({ name: null, text: '', source: 'default_prompt' });
        }
    };

    const handleToolChange = (toolKey) => {
        setCurrentTools(prev => ({...prev, [toolKey]: !prev[toolKey]}));
    };

    const renderPromptText = (text) => {
        if (!text) return '';
        const limit = 150;
        if (text.length <= limit) return text;
        if (showFullPromptText) return (<>{text}<button className="link-button" onClick={() => setShowFullPromptText(false)}>Less</button></>);
        else return (<>{`${text.substring(0, limit)}...`}<button className="link-button" onClick={() => setShowFullPromptText(true)}>More</button></>);
    };

    const isAgentActive = currentUseAgentAPI;
    const isAnyLLMToolActive = currentUseRAG || currentUseText2SQL;

    const handleToggleChange = (settingName, isChecked) => {
        if (isChecked) {
            if (settingName === 'useAgentAPI') {
                setCurrentUseAgentAPI(true);
                setCurrentUseRAG(false);
                setCurrentUseText2SQL(false);
            } else if (settingName === 'useRAG') {
                setCurrentUseRAG(true);
                setCurrentUseAgentAPI(false);
            } else if (settingName === 'useText2SQL') {
                setCurrentUseText2SQL(true);
                setCurrentUseAgentAPI(false);
            }
        } else {
            if (settingName === 'useAgentAPI') {
                setCurrentUseAgentAPI(false);
            } else if (settingName === 'useRAG') {
                setCurrentUseRAG(false);
            } else if (settingName === 'useText2SQL') {
                setCurrentUseText2SQL(false);
            }
        }
    };

    const handleConfirmAction = async () => {
        if (!actionToConfirm) return;
    
        if (actionToConfirm.type === 'deleteFile') {
            const { item } = actionToConfirm;
            setUploadStatus({ message: `Deleting '${item.name}'...`, type: 'loading' });
            try {
                const response = await axios.post(`${BASE_URL}/api/delete_user_files`, { filePaths: [item.path] }, { withCredentials: true });
                setUploadStatus({ message: response.data.message || 'Item deleted.', type: 'success' });
                setLastClickedFile(null); 
                fetchFiles(currentPath);
                fetchDirectorySize();
            } catch (error) {
                const errorMsg = error.response?.data?.error || error.message || 'Failed to delete item.';
                setUploadStatus({ message: errorMsg, type: 'error' });
            } finally {
                setTimeout(() => setUploadStatus({ message: '', type: '' }), 5000);
            }
        }
    
        setActionToConfirm(null);
        setShowConfirmationModal(false);
    };

    const handleCancelAction = () => {
        setActionToConfirm(null);
        setShowConfirmationModal(false);
    };

    const handleApplyAndCloseInternal = () => {
        console.log('ChatSettingsModal: Apply & Close clicked. Calling onSave with current modal states.');
        onSave( 
            currentLoops,
            currentTools,
            currentPipeline,
            currentSelectedDBInModal,
            currentSelectedKnowledgeNameInModal,
            selectedFilesForAttachment,
            currentUseRAG,
            currentUseText2SQL,
            currentUseAgentAPI,
            currentSeeThoughts 
        );
    };

    const handleCancelAndCloseInternal = () => {
        console.log('ChatSettingsModal: Close/Cancel triggered (X, ESC, or Overlay). Calling onCancel.');
        onCancel(); 
    };


    const mainTabs = ['LLM Settings', 'Agent Settings'];
    const activeModeMainTab = isAgentActive ? 'Agent Settings' : (isAnyLLMToolActive ? 'LLM Settings' : null);

    return (
        <>
            <Modal
                isOpen={isOpen && !isViewerOpen && !showConfirmationModal}
                onRequestClose={handleCancelAndCloseInternal}
                style={customModalStyles}
                contentLabel="Chat Settings Modal"
                ariaHideApp={false}
            >
                <div className="chat-settings-modal-header-layout">
                    <div className="chat-settings-modal-header">
                        <h2>Chat Settings</h2>
                        <button onClick={handleCancelAndCloseInternal} className="modal-close-button top-right" title="Close (Discard Changes)">×</button>
                    </div>
                    <div className="chat-settings-tabs-row">
                        <div className="chat-settings-tabs main-tabs">
                            {mainTabs.map((tab) => (
                                <button
                                    key={tab}
                                    className={`tab-button ${activeTab === tab ? 'active' : ''} ${activeModeMainTab === tab ? 'active-mode' : ''}`}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                        <div className="chat-settings-tabs file-explorer-tab-button-container">
                            <button
                                key="File Explorer"
                                className={`tab-button ${activeTab === 'File Explorer' ? 'active' : ''}`}
                                onClick={() => setActiveTab('File Explorer')}
                            >
                                File Explorer
                            </button>
                        </div>
                    </div>
                </div>

                {loadingError && ( <div className="modal-error" style={{ whiteSpace: 'pre-line' }}> {loadingError.trim()} </div> )}

                <div className={`chat-settings-tab-content ${ (activeModeMainTab === activeTab) ? 'active-mode-tint' : '' }`}>
                    {activeTab === 'LLM Settings' && (
                        <>
                            <div className="settings-section">
                                <h3>Retrieval Options</h3>
                                <div className="setting-item checkbox">
                                    <input id="useRAG" type="checkbox" checked={currentUseRAG}
                                        onChange={e => handleToggleChange('useRAG', e.target.checked)} />
                                    <label htmlFor="useRAG">Use RAG (Basic Retrieval)</label>
                                </div>
                                {currentUseRAG && (
                                    <div className="setting-item select">
                                        <label htmlFor="knowledgeNameModalSelect">Select Knowledge Source:</label>
                                        <select id="knowledgeNameModalSelect" value={currentSelectedKnowledgeNameInModal} onChange={(e) => setCurrentSelectedKnowledgeNameInModal(e.target.value)} disabled={!currentUseRAG} >
                                            <option value="">-- Select a Name --</option>
                                            {knowledgeNames.map((name, index) => ( <option key={index} value={name}> {name} </option> ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                            <div className="settings-section">
                                <h3>Text-to-SQL Options</h3>
                                <div className="setting-item checkbox">
                                    <input id="useText2SQL" type="checkbox" checked={currentUseText2SQL}
                                        onChange={e => handleToggleChange('useText2SQL', e.target.checked)} />
                                    <label htmlFor="useText2SQL">Use Text2SQL</label>
                                </div>
                                {currentUseText2SQL && (
                                    <div className="setting-item select">
                                        <label htmlFor="databaseSelectModal">Select Database:</label>
                                        <select id="databaseSelectModal" value={currentSelectedDBInModal} onChange={(e) => setCurrentSelectedDBInModal(e.target.value)} disabled={!currentUseText2SQL} >
                                            <option value="">-- Select a Database --</option>
                                            {uniqueRemoteDbs.length === 0 && !loadingError.includes('Databases') && <option value="" disabled>Loading...</option>}
                                            {uniqueRemoteDbs.map((dbName) => ( <option key={dbName} value={dbName}> {dbName} </option> ))}
                                        </select>
                                        {uniqueRemoteDbs.length === 0 && loadingError.includes('Databases') && <small style={{marginTop: '5px', color: 'gray'}}>Could not load list.</small>}
                                    </div>
                                )}
                            </div>
                            <div className="settings-section">
                                <h3>System Prompt</h3>
                                <div className="setting-item select">
                                    <label htmlFor="promptSelectLLMTab">Select System Prompt:</label>
                                    <select id="promptSelectLLMTab" onChange={handlePromptSelectChange} value={selectedPromptDetails?.id || ''} disabled={currentUseAgentAPI} >
                                        <option value="">-- Default Prompt --</option>
                                        {prompts.map(prompt => ( <option key={prompt.id} value={prompt.id}> {prompt.prompt_name} </option> ))}
                                    </select>
                                    {currentUseAgentAPI && <small style={{marginTop: '5px', color: 'gray'}}>Disabled: Agentic Workflow is active.</small>}
                                </div>
                                {selectedPromptDetails && ( <div className="prompt-details"> <h4>Selected: {selectedPromptDetails.prompt_name}</h4> <p className="prompt-text-display">{renderPromptText(selectedPromptDetails.prompt_text)}</p> </div> )}
                                {!selectedPromptDetails && <p><small>Using default system prompt.</small></p>}
                            </div>
                        </>
                    )}
                    {activeTab === 'Agent Settings' && (
                        <>
                            <div className="settings-section">
                                <h3>Agent Options</h3>
                                <div className="setting-item checkbox">
                                    <input id="useAgentApi" type="checkbox" checked={currentUseAgentAPI}
                                        onChange={e => handleToggleChange('useAgentAPI', e.target.checked)} />
                                    <label htmlFor="useAgentApi">Use Agentic Workflow</label>
                                </div>
                                {currentUseAgentAPI && (
                                    <>
                                        <div className="setting-item checkbox">
                                            <input id="seeThoughtsModal" type="checkbox" checked={currentSeeThoughts} onChange={e => setCurrentSeeThoughts(e.target.checked)} disabled={!currentUseAgentAPI} />
                                            <label htmlFor="seeThoughtsModal" title={!currentUseAgentAPI ? 'Enable "Use Agentic Workflow" first' : ''}>Show Agent Thoughts/Actions</label>
                                        </div>
                                        <div className="setting-item number-input">
                                            <label htmlFor="agentLoopsModal">Loops before Human Input:</label>
                                            <input id="agentLoopsModal" type="number" min="1" value={currentLoops} onChange={e => setCurrentLoops(Math.max(1, parseInt(e.target.value, 10) || 1))} disabled={!currentUseAgentAPI} />
                                        </div>
                                        <div className="setting-item select">
                                            <label htmlFor="pipelineSelectModal">Select Agent Pipeline (Optional):</label>
                                            <select id="pipelineSelectModal" value={currentPipeline} onChange={(e) => setCurrentPipeline(e.target.value)} disabled={!currentUseAgentAPI} >
                                                <option value="">-- No Pipeline --</option>
                                                {(pipelines || []).map(pipeline => ( <option key={pipeline.id} value={pipeline.name}> {pipeline.name} </option> ))}
                                            </select>
                                        </div>
                                    </>
                                )}
                            </div>
                            {/* <div className="settings-section">
                                <h3>Agent Tools (Active if "Use Agentic Workflow" is checked)</h3>
                                <div className="tools-group tools-columns">
                                    <div className="tools-column">
                                        <h4>Retrieval tools</h4>
                                        <div className="tools-grid">
                                            {TOOL_GROUPS.retrieval.map(toolKey => (
                                                <div className="setting-item checkbox tool-item" key={toolKey}>
                                                    <input id={`tool-${toolKey}`} type="checkbox" checked={!!currentTools[toolKey]} onChange={() => handleToolChange(toolKey)} disabled={!currentUseAgentAPI} />
                                                    <label htmlFor={`tool-${toolKey}`} title={TOOL_DESCRIPTIONS[toolKey] || toolKey}> {toolKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="tools-column">
                                        <h4>File tools</h4>
                                        <div className="tools-grid">
                                            {TOOL_GROUPS.file.map(toolKey => (
                                                <div className="setting-item checkbox tool-item" key={toolKey}>
                                                    <input id={`tool-${toolKey}`} type="checkbox" checked={!!currentTools[toolKey]} onChange={() => handleToolChange(toolKey)} disabled={!currentUseAgentAPI} />
                                                    <label htmlFor={`tool-${toolKey}`} title={TOOL_DESCRIPTIONS[toolKey] || toolKey}> {toolKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="tools-column">
                                        <h4>Calculations/Analysis tools</h4>
                                        <div className="tools-grid">
                                            {TOOL_GROUPS.analysis.map(toolKey => (
                                                <div className="setting-item checkbox tool-item" key={toolKey}>
                                                    <input id={`tool-${toolKey}`} type="checkbox" checked={!!currentTools[toolKey]} onChange={() => handleToolChange(toolKey)} disabled={!currentUseAgentAPI} />
                                                    <label htmlFor={`tool-${toolKey}`} title={TOOL_DESCRIPTIONS[toolKey] || toolKey}> {toolKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div> */}
                            <div className="settings-section">
                                <h3>Agent Tools (Active if "Use Agentic Workflow" is checked)</h3>
                                <div className="tools-group tools-columns">
                                    {Object.entries(TOOL_GROUPS).map(([groupName, toolKeys]) => (
                                        <div className="tools-column" key={groupName}>
                                            <h4>{groupName.charAt(0).toUpperCase() + groupName.slice(1)} tools</h4>
                                            <div className="tools-grid">
                                                {toolKeys.map(toolKey => {
                                                    // --- Special logic for the consolidated "File Tools" ---
                                                    if (toolKey === 'FileTools') {
                                                        const areAllFileToolsSelected = FILE_TOOLS_GROUP_KEYS.every(key => !!currentTools[key]);
                                                        return (
                                                            <div className="setting-item checkbox tool-item" key={toolKey}>
                                                                <input
                                                                    id={`tool-group-file`}
                                                                    type="checkbox"
                                                                    checked={areAllFileToolsSelected}
                                                                    onChange={(e) => handleFileToolsGroupChange(e.target.checked)}
                                                                    disabled={!currentUseAgentAPI}
                                                                />
                                                                <label htmlFor={`tool-group-file`} title={TOOL_DESCRIPTIONS[toolKey] || toolKey}>
                                                                    Enable All File Tools
                                                                </label>
                                                            </div>
                                                        );
                                                    }
                                                    // --- Special logic for the consolidated "Search & Scrape Tools" ---
                                                    if (toolKey === 'SearchScrapeTools') {
                                                        const areAllSearchToolsSelected = SEARCH_SCRAPE_GROUP_KEYS.every(key => !!currentTools[key]);
                                                        return (
                                                            <div className="setting-item checkbox tool-item" key={toolKey}>
                                                                <input
                                                                    id={`tool-group-search`}
                                                                    type="checkbox"
                                                                    checked={areAllSearchToolsSelected}
                                                                    onChange={(e) => handleSearchScrapeGroupChange(e.target.checked)}
                                                                    disabled={!currentUseAgentAPI}
                                                                />
                                                                <label htmlFor={`tool-group-search`} title="Enable all search and web scraping tools.">
                                                                    Enable All Search/Scrape
                                                                </label>
                                                            </div>
                                                        );
                                                    }
                                                    
                                                    // --- Default rendering for all other tools ---
                                                    return (
                                                        <div className="setting-item checkbox tool-item" key={toolKey}>
                                                            <input
                                                                id={`tool-${toolKey}`}
                                                                type="checkbox"
                                                                checked={!!currentTools[toolKey]}
                                                                onChange={() => handleToolChange(toolKey)}
                                                                disabled={!currentUseAgentAPI}
                                                            />
                                                            <label htmlFor={`tool-${toolKey}`} title={TOOL_DESCRIPTIONS[toolKey] || toolKey}>
                                                                {toolKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                                            </label>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="settings-section">
                                <h3>System Prompt</h3>
                                <div className="setting-item select">
                                    <label htmlFor="promptSelectAgentTab">Select System Prompt:</label>
                                    <select id="promptSelectAgentTab" onChange={handlePromptSelectChange} value={selectedPromptDetails?.id || ''} disabled={isAnyLLMToolActive} >
                                        <option value="">-- Default Prompt --</option>
                                        {prompts.map(prompt => ( <option key={prompt.id} value={prompt.id}> {prompt.prompt_name} </option> ))}
                                    </select>
                                    {isAnyLLMToolActive && <small style={{marginTop: '5px', color: 'gray'}}>Disabled: LLM Tools are active.</small>}
                                </div>
                                {selectedPromptDetails && ( <div className="prompt-details"> <h4>Selected: {selectedPromptDetails.prompt_name}</h4> <p className="prompt-text-display">{renderPromptText(selectedPromptDetails.prompt_text)}</p> </div> )}
                                {!selectedPromptDetails && <p><small>Using default system prompt.</small></p>}
                            </div>
                        </>
                    )}
                    {activeTab === 'File Explorer' && (
                        <div className="file-explorer-tab">
                            <div className="file-explorer-header"> <h3>File Explorer (User Home)</h3> <span className="directory-size-info" title={`Total size: ${directorySize.bytes} bytes`}> Size: {isLoadingSize ? 'Calculating...' : directorySize.human} </span> </div>
                            <div className="file-explorer-toolbar">
                                <button onClick={handleNavigateBackInternal} disabled={currentPath === '.' || isLoadingFiles}> ← Back </button>
                                <span className="current-path-display" title={`Current Path: ~/Home/${currentPath === '.' ? '' : currentPath}`}> ~/Home/{currentPath === '.' ? '' : currentPath} </span>
                                <button onClick={handleUploadButtonClickInternal} disabled={isLoadingFiles || uploadStatus.type === 'loading'} className="upload-button" title={`Upload files to current directory`}> ↑ Upload </button>
                                <button onClick={handleOpenFileInternal} disabled={!lastClickedFile || lastClickedFile.type === 'dir' || isLoadingFiles || isViewerOpen} className="open-file-button" title={!lastClickedFile || lastClickedFile.type === 'dir' ? "Select a file first" : "Open selected file"} > Open </button>
                                <button onClick={handleDeleteClickInternal} disabled={!lastClickedFile || isLoadingFiles} className="delete-button" title={!lastClickedFile ? "Select an item to delete" : `Delete '${lastClickedFile.name}'`}> Delete </button>
                                <span className="attachment-info"> {selectedFilesForAttachment.length} attached </span>
                                <input type="file" multiple ref={fileInputRef} onChange={handleFileSelectedForUploadInternal} className="hidden-file-input" />
                            </div>
                            <div className="file-list-headers">
                                <div className="file-header name-header" onClick={() => requestSort('name')}> Name {sortConfig.key === 'name' && (sortConfig.direction === 'ascending' ? <SortAscIcon /> : <SortDescIcon />)} </div>
                                <div className="file-header modified-header" onClick={() => requestSort('lastModified')}> Date Modified {sortConfig.key === 'lastModified' && (sortConfig.direction === 'ascending' ? <SortAscIcon /> : <SortDescIcon />)} </div>
                                <div className="file-header type-header" onClick={() => requestSort('type')}> Type {sortConfig.key === 'type' && (sortConfig.direction === 'ascending' ? <SortAscIcon /> : <SortDescIcon />)} </div>
                            </div>
                            <div className="file-list-container">
                                {uploadStatus.message && ( <div className={`upload-status ${uploadStatus.type}`}> {uploadStatus.message} </div> )}
                                {fileError && <div className="file-error-message">{fileError}</div>}
                                {isLoadingFiles && <div className="file-loading">Loading files...</div>}
                                <ul className="file-list">
                                    {!isLoadingFiles && sortedItems.length === 0 && !fileError && ( <li className="empty-folder">This folder is empty.</li> )}
                                    {sortedItems.map((item) => {
                                        const fullPath = buildFullPath(currentPath, item.name);
                                        const isSelectedForAttachment = selectedFilesForAttachment.includes(fullPath);
                                        const isLastClickedFileItem = lastClickedFile?.path === fullPath;
                                        return (
                                            <li key={`${item.name}-${item.lastModified || 'no-date'}`} className={`file-item ${item.type} ${isSelectedForAttachment ? 'selected-attachment' : ''} ${isLastClickedFileItem ? 'last-clicked' : ''}`} onClick={() => handleFileItemClickInternal(item)} onDoubleClick={() => handleFileItemDoubleClickInternal(item)} title={`Name: ${item.name}\nType: ${item.type}\nModified: ${item.lastModified || 'N/A'}\nDouble-click to ${item.type === 'dir' ? 'enter' : 'attach/detach'}`} >
                                                <span className="file-item-icon-name"> {item.type === 'dir' ? <FolderIcon /> : <FileIcon />} <span className="file-name">{item.name}</span> </span>
                                                <span className="file-last-modified">{item.lastModified && item.lastModified !== "N/A" ? item.lastModified : ''}</span>
                                                <span className="file-type-indicator">{item.type}</span>
                                                {isSelectedForAttachment && <span className="attachment-indicator" title="Attached">✓</span>}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
                <div className="chat-settings-modal-actions">
                    <button onClick={handleApplyAndCloseInternal} className="modal-button primary">Apply & Close</button>
                </div>
            </Modal>
            <DocumentViewerModal isOpen={isViewerOpen} onClose={() => setIsViewerOpen(false)} filePath={viewingFilePath} username={authUsername} />
            <Modal
                isOpen={showConfirmationModal}
                onRequestClose={handleCancelAction}
                style={customModalStyles}
                contentLabel="Confirm Action Modal"
                ariaHideApp={false}
                className="confirmation-modal"
                overlayClassName="confirmation-overlay"
            >
                <div className="modal-header"> <h3>Confirm Action</h3> <button onClick={handleCancelAction} className="modal-close-button">×</button> </div>
                <div className="modal-content"> <p>{actionToConfirm?.message || 'Are you sure?'}</p> </div>
                <div className="modal-actions"> <button onClick={handleConfirmAction} className="modal-button primary">Yes, Proceed</button> <button onClick={handleCancelAction} className="modal-button">Cancel</button> </div>
            </Modal>
        </>
    );
};

// // Default TOOL_GROUPS and TOOL_DESCRIPTIONS 
// const TOOL_GROUPS = {
//     retrieval: ['relevantText', 'searchCollectionKnowledge', 'collection_knowledge', 'exploreKnowledgeGraph', 'googlesearch', 'queryDatabases', 'Knowledge_query'],
//     file: ['saveTextToFile', 'listFilesInDirectory', 'createDirectory', 'readFileContent', 'listAllMyFiles', 'image_annotation', 'generate_image'],
//     analysis: ['analyticsEngine']
// };
// const TOOL_DESCRIPTIONS = {
//     relevantText: "Return relevant text from articles in a collection based on user input.",
//     searchCollectionKnowledge: "Search a knowledge graph collection for facts/statements using keywords.",
//     collection_knowledge: "List graph facts/statements from a selected collection and use these to answer a question.",
//     exploreKnowledgeGraph: "Explore a knowledge graph collection and synthesize an answer.",
//     googlesearch: "Search Google for given keywords.",
//     queryDatabases: "Query a database for information based on your question.",
//     saveTextToFile: "Save provided text to a file in the File Explorer.",
//     listFilesInDirectory: "List files in a specified directory in the File Explorer.",
//     readFileContent: "Read and return the content of a file in the File Explorer.",
//     analyticsEngine: "Perform calculations or data analysis using Python code.",
//     Knowledge_query: "Query the Pearl Database for documents and information.",
//     listAllMyFiles: "Provides a complete list of all file and directory paths within your personal Home workspace.",
//     image_annotation: "Analyzes an image based on a query. Requires 'image_file_path' (relative to user's Home, e.g., 'Pictures/my_image.jpg') and 'query' (text prompt for the image). ex. {'image_file_path': 'Images/cat.jpg', 'query': 'What color is the cat?'}",
//     generate_image: "Generates an image based on a detailed textual prompt using DALL-E. Saves the image to the user's Home/Images directory with a specified filename.",
//     createDirectory: "Use to create a new directory inside the user's Home workspace. This can also create nested directories. Provide the name for the new directory. Example: {'directory_name': 'MyNewProject'}",
// };

// --- Add this new constant to define the tools in the group ---
const FILE_TOOLS_GROUP_KEYS = ['saveTextToFile', 'listFilesInDirectory', 'readFileContent', 'listAllMyFiles', 'createDirectory'];

const SEARCH_SCRAPE_GROUP_KEYS = ['googlesearch', 'scrapeWebsite'];

// --- Replace the existing TOOL_GROUPS constant with this ---
const TOOL_GROUPS = {
    retrieval: ['relevantText', 'exploreKnowledgeGraph', 'Knowledge_query', 'queryDatabases'],
    'search & scrape': ['SearchScrapeTools'], // NEW GROUP
    file: ['FileTools'],
    media: ['image_annotation', 'generate_image'],
    analysis: ['analyticsEngine'],
    // stocks: ['getStockInformation']
};

// --- Replace the existing TOOL_DESCRIPTIONS constant with this ---
const TOOL_DESCRIPTIONS = {
    FileTools: "A single option to enable all core file system operations: save, list, read, create directories, and list all personal files.", // New description for the meta-tool
    relevantText: "Return relevant text from articles in a collection based on user input.",
    searchCollectionKnowledge: "Search a knowledge graph collection for facts/statements using keywords.",
    exploreKnowledgeGraph: "Explore a knowledge graph collection and synthesize an answer.",
    googlesearch: "Search Google for given keywords.",
    queryDatabases: "Query a database for information based on your question.",
    saveTextToFile: "Save provided text to a file in the File Explorer.",
    listFilesInDirectory: "List files in a specified directory in the File Explorer.",
    readFileContent: "Read and return the content of a file in the File Explorer.",
    analyticsEngine: "Perform calculations or data analysis using Python code.",
    Knowledge_query: "Query the Pearl Database for documents and information.",
    listAllMyFiles: "Provides a complete list of all file and directory paths within your personal Home workspace.",
    image_annotation: "Analyzes an image based on a query. Requires 'image_file_path' (relative to user's Home, e.g., 'Pictures/my_image.jpg') and 'query' (text prompt for the image). ex. {'image_file_path': 'Images/cat.jpg', 'query': 'What color is the cat?'}",
    generate_image: "Generates an image based on a detailed textual prompt using DALL-E. Saves the image to the user's Home/Images directory with a specified filename.",
    createDirectory: "Use to create a new directory inside the user's Home workspace. This can also create nested directories. Provide the name for the new directory. Example: {'directory_name': 'MyNewProject'}",
    scrapeWebsite: "Use to scrape the text content from a given web URL. Expects a 'url' parameter. ex. {'url': 'https://en.wikipedia.org/wiki/Python_(programming_language)'}",
    getStockInformation: "Fetches historical stock market data for a list of one or more stock tickers from a specified start date. ex. {'tickers': ['AAPL', 'MSFT', 'NVDA'], 'start_date': '2025-07-07'}",
};

export default ChatSettingsModal;